import { z } from 'zod';
import { isoDateSchema } from './iso-date.ts';

/**
 * Per-partner website traffic, as GA4 reports it.
 *
 * Traffic and sales are two separate measurements joined on the partner, not on the
 * order: UTM tags measure visits, discount codes attribute sales. Anything that reads
 * both must say which figure came from which, because they will not reconcile — a
 * visitor can buy days later on another device, and a code can be used by someone who
 * never clicked a link.
 */

export const trafficGranularitySchema = z.enum(['day', 'hour']);
export type TrafficGranularity = z.infer<typeof trafficGranularitySchema>;

/** A partner's configured tags. Both are needed: a source alone is ambiguous. */
export const partnerUtmSchema = z.object({
  partnerId: z.string().min(1),
  source: z.string().min(1),
  campaign: z.string().min(1),
});
export type PartnerUtmConfig = z.infer<typeof partnerUtmSchema>;

export const trafficQuerySchema = z
  .object({
    partners: z.array(partnerUtmSchema).min(1).max(200),
    from: isoDateSchema,
    to: isoDateSchema,
    granularity: trafficGranularitySchema.default('day'),
  })
  .refine((q) => q.from <= q.to, { message: 'from must not be after to', path: ['from'] });
export type TrafficQuery = z.infer<typeof trafficQuerySchema>;

export const trafficMetricsSchema = z.object({
  sessions: z.number(),
  activeUsers: z.number(),
  newUsers: z.number(),
  /**
   * GA4's definition: 1 − engagementRate, where a session is engaged if it lasts 10
   * seconds, fires a key event, or has 2+ page views. NOT Universal Analytics'
   * single-interaction bounce, and not comparable to a UA-era baseline.
   */
  bounceRate: z.number(),
  engagementRate: z.number(),
  /** Seconds. */
  averageSessionDuration: z.number(),
  pagesPerSession: z.number(),
  /** Renamed from "conversions" in 2024. */
  keyEvents: z.number(),
});
export type TrafficMetrics = z.infer<typeof trafficMetricsSchema>;

/**
 * GA4's own spelling for a session whose medium it could not determine.
 *
 * Kept as GA4 writes it rather than translated to a word of our own, so a figure in
 * the portal can be traced back to a row in the GA4 UI. The label a human reads is the
 * UI's business.
 */
export const UNSET_MEDIUM = '(not set)';

/**
 * Normalises a medium for storage and comparison.
 *
 * Lower-cased because GA4 matches case-insensitively and a legacy row could carry
 * different casing — `Social` and `social` are one channel, not two. An empty value
 * becomes the explicit sentinel rather than an empty string, which would be
 * indistinguishable from a missing column.
 */
export function normalizeMedium(raw: string | null | undefined): string {
  const trimmed = (raw ?? '').trim().toLowerCase();
  return trimmed === '' || trimmed === '(none)' ? UNSET_MEDIUM : trimmed;
}

/**
 * One (medium, time) cell — the grain the whole traffic pipeline stores.
 *
 * The medium is part of the grain rather than a separate rollup so that the partner's
 * total and its per-channel breakdown are two views of ONE row set and cannot disagree.
 * A separate totals table would drift the first time one of the two writes failed.
 */
export const trafficBucketSchema = trafficMetricsSchema.extend({
  /** YYYY-MM-DD for day granularity, YYYY-MM-DDTHH for hour. */
  bucket: z.string(),
  /** `sessionMedium`, normalised — the link's channel, or UNSET_MEDIUM. */
  medium: z.string(),
});
export type TrafficBucket = z.infer<typeof trafficBucketSchema>;

/** A channel's share of the window. Derived from the buckets, never stored apart. */
export const trafficMediumTotalsSchema = z.object({
  medium: z.string(),
  totals: trafficMetricsSchema,
});
export type TrafficMediumTotals = z.infer<typeof trafficMediumTotalsSchema>;

/**
 * Sums traffic rows into one figure.
 *
 * **Counts add; rates and averages do not.** A bucket with four sessions must not pull
 * the engagement rate as hard as one with four hundred, so the rates are re-derived
 * session-weighted from the counts underneath. Averaging the per-row rates is the
 * mistake this function exists to prevent, and it is invisible in test data where every
 * row happens to carry the same number of sessions.
 *
 * Lives in contracts because four places needed it: the GA4 service folding a window,
 * the same service splitting it by channel, the report service folding stored rows for
 * the tiles, and the demo generator. All four had their own copy.
 *
 * The input omits `bounceRate` because this function DERIVES it. Requiring one would
 * have forced a derived column onto the stored read row, which holds only what GA4
 * actually reported — and two copies of one fact can disagree.
 */
export type TrafficMetricsInput = Omit<TrafficMetrics, 'bounceRate'>;

export function sumMetrics(rows: readonly TrafficMetricsInput[]): TrafficMetrics {
  let sessions = 0;
  let activeUsers = 0;
  let newUsers = 0;
  let keyEvents = 0;
  let engaged = 0;
  let durationSeconds = 0;
  let pageViews = 0;

  for (const row of rows) {
    sessions += row.sessions;
    activeUsers += row.activeUsers;
    newUsers += row.newUsers;
    keyEvents += row.keyEvents;
    engaged += row.engagementRate * row.sessions;
    durationSeconds += row.averageSessionDuration * row.sessions;
    pageViews += row.pagesPerSession * row.sessions;
  }

  const engagementRate = sessions > 0 ? engaged / sessions : 0;

  return {
    sessions,
    // Users cannot be summed across rows without double-counting anyone who returned,
    // so this is an upper bound. GA4's de-duplicated figure needs a query with no time
    // dimension; every screen shows sessions.
    activeUsers,
    newUsers,
    engagementRate,
    // GA4 defines bounce as 1 − engagement, so it is derived here rather than summed —
    // two stored copies of one fact can disagree.
    bounceRate: sessions > 0 ? 1 - engagementRate : 0,
    averageSessionDuration: sessions > 0 ? durationSeconds / sessions : 0,
    pagesPerSession: sessions > 0 ? pageViews / sessions : 0,
    keyEvents,
  };
}

export const partnerTrafficSchema = z.object({
  partnerId: z.string(),
  source: z.string(),
  campaign: z.string(),
  totals: trafficMetricsSchema,
  buckets: z.array(trafficBucketSchema),
  /** Sorted by sessions, busiest channel first. */
  mediums: z.array(trafficMediumTotalsSchema),
});
export type PartnerTraffic = z.infer<typeof partnerTrafficSchema>;

export const trafficReportSchema = z.object({
  from: z.string(),
  to: z.string(),
  granularity: trafficGranularitySchema,
  partners: z.array(partnerTrafficSchema),
  /**
   * Quality flags that must reach the UI rather than being swallowed.
   *
   * `provisional` marks a window that includes today: GA4 is still processing, so the
   * numbers will change. `samplingRatio` is non-null when the report is an estimate.
   * `otherRowPresent` means the long tail was bucketed and a filtered figure may be
   * short.
   */
  quality: z.object({
    provisional: z.boolean(),
    samplingRatio: z.number().nullable(),
    otherRowPresent: z.boolean(),
    thresholded: z.boolean(),
  }),
  /** Rows GA4 returned that matched no configured partner pair. */
  discardedRows: z.number(),
});
export type TrafficReport = z.infer<typeof trafficReportSchema>;
